'use client';

import { useEffect, useState } from 'react';

import { ProductModel } from '@bytel/leviator';
import { Divider, Justify, Text, TextLevels, TypographyBold, View } from '@bytel/trilogy-react';

import { getApiProductDetails } from '@services/products';

import { DiscountEnumType } from '@app-types/discount';
import { PriceFundingsDetails, Product, ProductChildFull } from '@app-types/product';

import { InlinePrice } from '@components/inline-price';

import { getDiscountValue } from '@helpers/product';
import { noop } from '@helpers/try-catch';

type PriceDetailProps = {
    product: { phone: ProductModel; details?: PriceFundingsDetails } | Product | ProductChildFull;
};

export function PriceDetailAccessory({ product }: Readonly<PriceDetailProps>) {
    const [file, setFile] = useState<string>();

    const gencode = 'phone' in product ? product.phone.gencode : product.gencode;

    const metadata = 'phone' in product ? product.phone.metadata : product;

    useEffect(() => {
        if (metadata?.urlKey) {
            getApiProductDetails(metadata.urlKey)
                .then((result) => {
                    setFile(result.odr?.file);
                })
                .catch(noop);
        }
    }, [metadata?.urlKey]);

    if (!product.details || !gencode) return null;

    const discounts = product.details.discounts;
    const instantDiscount = product.details.price.initial - product.details.price.final;

    const sumODRDiscount = getDiscountValue(discounts, DiscountEnumType.ODR);

    return (
        <>
            <View flexable justify={Justify.SPACE_BETWEEN}>
                <Text level={TextLevels.TWO}>
                    {metadata?.brand} {metadata?.name}
                </Text>
                <InlinePrice typo={[]} level={TextLevels.TWO} amount={product.details.price.initial} />
            </View>
            {instantDiscount > 0 && (
                <View flexable justify={Justify.SPACE_BETWEEN}>
                    <Text level={TextLevels.TWO} marginless>
                        Remise immédiate
                    </Text>
                    <InlinePrice typo={[]} level={TextLevels.TWO} amount={-instantDiscount} />
                </View>
            )}
            <Divider />
            <View flexable justify={Justify.SPACE_BETWEEN}>
                <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                    Prix de l&apos;accessoire
                </Text>
                <Text level={TextLevels.ONE} marginless typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                    =&nbsp;
                    <InlinePrice amount={product.details.price.final ?? 0} />
                </Text>
            </View>
            {sumODRDiscount > 0 && (
                <>
                    <View flexable justify={Justify.SPACE_BETWEEN}>
                        <div>
                            <Text level={TextLevels.TWO} marginless>
                                Après remboursement sur demande
                            </Text>
                            {file ? (
                                <Text level={TextLevels.THREE} marginless>
                                    <a className='link' download href={file}>
                                        Télécharger le coupon
                                    </a>
                                </Text>
                            ) : null}
                        </div>

                        <InlinePrice typo={[]} level={TextLevels.TWO} amount={-sumODRDiscount} />
                    </View>
                    <Divider />
                    <View flexable justify={Justify.SPACE_BETWEEN}>
                        <Text level={TextLevels.ONE} marginless typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                            Prix après remboursement
                        </Text>
                        <Text level={TextLevels.ONE} marginless typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                            =&nbsp;
                            <InlinePrice amount={product.details.price.final - sumODRDiscount} />
                        </Text>
                    </View>
                </>
            )}
        </>
    );
}
